import React from "react";
import { useNavigate } from "react-router-dom";
import { Percent, ArrowRight, Layers, Sparkles } from "lucide-react";
import { DiscountCalculator } from "@/components/DiscountCalculator";
import { useI18n } from "@/context/I18nContext";

export function PricingSection() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const STEPS = [
    { modules: t("pricing.s1"), label: t("pricing.base"), value: "0 %", accent: false },
    { modules: t("pricing.s2"), label: t("pricing.discount"), value: "−10 %", accent: false },
    { modules: t("pricing.s3"), label: t("pricing.discount"), value: "−15 %", accent: false },
    { modules: t("pricing.s4"), label: t("pricing.discountMax"), value: "−20 %", accent: true },
  ];

  return (
    <section id="forfaits" className="relative py-24 lg:py-32" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#EAB308]">
            {t("pricing.eyebrow")}
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
            {t("pricing.titleA")} <span className="text-[#EAB308]">{t("pricing.titleB")}</span> {t("pricing.titleC")}
          </h2>
          <p className="mt-4 text-white/60 text-lg">
            {t("pricing.desc")}
          </p>
        </div>

        {/* Panneau rabais premium */}
        <div className="mt-12 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#12100a] via-[#0a0a0a] to-black">
          <div className="grid lg:grid-cols-5">
            {/* Bloc gauche : message */}
            <div className="lg:col-span-2 relative p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-white/10">
              <div className="absolute -top-10 -left-10 w-56 h-56 rounded-full bg-[#EAB308]/10 blur-3xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#dc143c]/40 bg-[#dc143c]/10 px-3 py-1.5 text-xs font-semibold text-[#f0607a]">
                  <Percent className="w-3.5 h-3.5" /> {t("pricing.badge")}
                </div>
                <h3 className="mt-5 text-2xl font-bold text-white leading-snug">
                  {t("pricing.save")} <span className="text-[#EAB308]">20 %</span> {t("pricing.saveEnd")}
                </h3>
                <ul className="mt-5 space-y-2.5 text-sm text-white/70">
                  <li className="flex items-center gap-2.5"><Layers className="w-4 h-4 text-[#EAB308]" /> {t("pricing.li1")}</li>
                  <li className="flex items-center gap-2.5"><Sparkles className="w-4 h-4 text-[#EAB308]" /> {t("pricing.li2")}</li>
                  <li className="flex items-center gap-2.5"><Sparkles className="w-4 h-4 text-[#EAB308]" /> {t("pricing.li3")}</li>
                </ul>
                <button
                  data-testid="pricing-see-all-cta"
                  onClick={() => navigate("/tarification")}
                  className="group mt-7 inline-flex items-center gap-2 rounded-xl bg-[#EAB308] px-7 py-4 text-base font-bold text-black transition-transform hover:-translate-y-0.5"
                >
                  {t("pricing.seeAll")}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>

            {/* Bloc droit : paliers de rabais */}
            <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5">
              {STEPS.map((s) => (
                <div
                  key={s.modules}
                  data-testid={`discount-step-${s.value}`}
                  className={`flex flex-col items-start justify-between p-6 lg:p-7 ${
                    s.accent ? "bg-[#EAB308]/[0.07]" : "bg-[#0a0a0a]"
                  }`}
                >
                  <span className="text-[11px] font-mono uppercase tracking-wider text-white/40">{s.modules}</span>
                  <div className="mt-6">
                    <div className={`text-3xl font-extrabold ${s.accent ? "text-[#EAB308]" : "text-white"}`}>{s.value}</div>
                    <div className="mt-1 text-xs text-white/45">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-5 text-sm text-white/40">
          {t("pricing.foot")}
        </p>

        {/* Calculateur interactif à la carte */}
        <div className="mt-12" id="calculateur">
          <DiscountCalculator />
        </div>
      </div>
    </section>
  );
}
